import React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatDuration, formatNumber } from './formatters';

/**
 * Componente reusable para visualizar percentiles de un objeto del estilo:
 *   { count, min, max, mean, p50, p95, p99 }
 *
 * Diseño: barras horizontales agrupadas comparando dos series (`done` vs
 * `failed`), una barra por percentil. Esto permite ver de un vistazo si
 * los `failed` están tomando significativamente más tiempo que los `done`,
 * patrón típico de timeouts o zombies.
 *
 * - `groups`: { done: {...}, failed: {...}, total?: {...} } shape del endpoint.
 * - `metrics`: array de keys a graficar (ej. ['mean', 'p50', 'p95', 'p99']).
 * - `slaSeconds`: opcional, dibuja una ReferenceLine vertical en ese valor.
 * - `logScale`: si true, usa escala logarítmica en el eje X (para queue_wait
 *   con outliers de varios órdenes de magnitud).
 */

const SERIES_COLORS = {
  done: '#10B981',
  failed: '#DC2626',
};

const SERIES_LABEL = {
  done: 'Done',
  failed: 'Failed',
};

const METRIC_LABEL = {
  min: 'Min',
  mean: 'Promedio',
  p50: 'p50',
  p95: 'p95',
  p99: 'p99',
  max: 'Max',
};

const buildChartData = (groups, metrics) => {
  const series = ['done', 'failed'];
  return metrics.map((m) => {
    const row = { metric: METRIC_LABEL[m] || m, _key: m };
    series.forEach((s) => {
      const v = Number(groups?.[s]?.[m]);
      row[s] = isFinite(v) && v > 0 ? v : null;
    });
    return row;
  });
};

const renderTooltip = (groups) => ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div
      className="recharts-default-tooltip"
      style={{
        background: 'var(--surface-primary)',
        border: '1px solid var(--border-primary)',
        padding: 10,
        borderRadius: 6,
        minWidth: 220,
      }}
    >
      <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>
        {label}
      </div>
      {payload.map((p) => {
        const seriesKey = p.dataKey;
        const grp = groups?.[seriesKey] || {};
        return (
          <div key={seriesKey} style={{ marginBottom: 4 }}>
            <div style={{ color: SERIES_COLORS[seriesKey], fontWeight: 600, fontSize: 12 }}>
              {SERIES_LABEL[seriesKey]} — {formatDuration(p.value)}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
              count: {formatNumber(grp.count)} · min: {formatDuration(grp.min)} · max:{' '}
              {formatDuration(grp.max)}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const PercentileBars = ({
  groups,
  metrics = ['mean', 'p50', 'p95', 'p99'],
  slaSeconds,
  logScale = false,
  height = 280,
}) => {
  const chartData = buildChartData(groups, metrics);

  const allValues = chartData
    .flatMap((r) => [r.done, r.failed])
    .filter((v) => typeof v === 'number' && v > 0);

  const minVal = allValues.length ? Math.min(...allValues) : 0.1;
  const maxVal = allValues.length ? Math.max(...allValues) : 1;

  // Para log scale recharts requiere un dominio explicito > 0.
  const xAxisProps = logScale
    ? {
        scale: 'log',
        domain: [Math.max(0.05, minVal * 0.5), Math.max(maxVal * 1.5, 1)],
        type: 'number',
        tickFormatter: (v) => formatDuration(v),
        allowDataOverflow: true,
        ticks: undefined,
      }
    : {
        type: 'number',
        tickFormatter: (v) => formatDuration(v),
      };

  return (
    <div style={{ height, width: '100%' }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 8, right: 24, bottom: 8, left: 16 }}
          barGap={4}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis {...xAxisProps} />
          <YAxis dataKey="metric" type="category" width={80} tick={{ fontSize: 12 }} />
          <Tooltip content={renderTooltip(groups)} cursor={{ fill: 'transparent' }} />
          <Legend />
          {slaSeconds != null && (
            <ReferenceLine
              x={slaSeconds}
              stroke="#F59E0B"
              strokeDasharray="4 4"
              label={{
                value: `SLA ${formatDuration(slaSeconds)}`,
                fill: '#F59E0B',
                fontSize: 11,
                position: 'top',
              }}
            />
          )}
          <Bar dataKey="done" name={SERIES_LABEL.done} radius={[0, 3, 3, 0]}>
            {chartData.map((_, i) => (
              <Cell key={`d-${i}`} fill={SERIES_COLORS.done} />
            ))}
          </Bar>
          <Bar dataKey="failed" name={SERIES_LABEL.failed} radius={[0, 3, 3, 0]}>
            {chartData.map((_, i) => (
              <Cell key={`f-${i}`} fill={SERIES_COLORS.failed} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default PercentileBars;
